import { baseBorderScale, BorderStyle, Color } from '../../theme/tokens';
import { Theme, CSSRulesFunction } from '../../theme/types';
import {
  AppearanceProps,
  ButtonColorVariant,
  ButtonColorVariantStyles,
  ButtonSize,
  SizeStyling,
} from './types';

export const commonButtonStyle: CSSRulesFunction<AppearanceProps> = theme => {
  return {
    display: 'inline-flex',
    boxSizing: 'border-box',
    alignItems: 'center',
    verticalAlign: 'middle',
    justifyContent: 'center',
    cursor: 'pointer',
    outline: 'none',
    // Removes weird margins in Safari
    marginLeft: 0,
    marginRight: 0,
    marginTop: 0,
    marginBottom: 0,
    // Remove default styling of browser
    // should be moved to global file
    ...theme.border.width(0),
    ...theme.border.color(Color.Transparent),
    ...theme.border.style(BorderStyle.Solid),
    /*
      // Need to move these into DesignToken
      @value RadiusXS 1px;
      @value RadiusSM 3px;
      @value RadiusMD 6px;
      @value RadiusLG 10px;
      @value RadiusXL 18px;
    */
    ...theme.radius(1.5),
  };
};

export const colorVariantStyling: Record<
  ButtonColorVariant,
  {
    base: ButtonColorVariantStyles;
    hover: ButtonColorVariantStyles;
    focus: ButtonColorVariantStyles;
    active: ButtonColorVariantStyles;
    disabled: ButtonColorVariantStyles;
  }
> = {
  [ButtonColorVariant.Primary]: {
    base: {
      backgroundColor: Color.Secondary_400,
      color: Color.White,
    },
    hover: {
      backgroundColor: Color.Secondary_600,
    },
    focus: {
      color: Color.White,
      backgroundColor: Color.Secondary_600,
      borderColor: Color.White,
      borderWidth: 0.5,
      outlineColor: Color.Secondary_600,
      outlineWidth: 1,
    },
    active: {},
    disabled: {
      color: Color.White,
      backgroundColor: Color.NeutralGrey_200,
    },
  },
  [ButtonColorVariant.Secondary]: {
    base: {
      backgroundColor: Color.White,
      color: Color.Secondary_400,
      borderColor: Color.Secondary_400,
      borderWidth: 0.5,
    },
    hover: {
      backgroundColor: Color.Secondary_50,
      color: Color.Secondary_600,
    },
    focus: {
      backgroundColor: Color.Secondary_50,
      color: Color.Secondary_600,
      borderColor: Color.Secondary_600,
      borderWidth: 1.5,
    },
    active: {},
    disabled: {
      color: Color.NeutralGrey_200,
      backgroundColor: Color.White,
      borderColor: Color.NeutralGrey_200,
    },
  },
  [ButtonColorVariant.Tertiary]: {
    base: {
      backgroundColor: Color.Transparent,
      color: Color.Secondary_400,
    },
    hover: {
      backgroundColor: Color.Secondary_50,
      color: Color.Secondary_600,
      borderWidth: 0.5,
      borderColor: Color.Secondary_600,
    },
    focus: {
      backgroundColor: Color.Secondary_50,
      color: Color.Secondary_600,
      borderColor: Color.Secondary_600,
      borderWidth: 1.5,
    },
    active: {},
    disabled: {
      color: Color.NeutralGrey_200,
      backgroundColor: Color.White,
    },
  },
  [ButtonColorVariant.Warning]: {
    base: {
      backgroundColor: Color.White,
      color: Color.Primary_900,
      borderColor: Color.Error_700,
    },
    hover: {
      backgroundColor: Color.Primary_50,
      color: Color.Error_700,
    },
    focus: {
      backgroundColor: Color.Primary_50,
      color: Color.Error_700,
      borderColor: Color.Error_400,
      borderWidth: 1.5,
    },
    active: {},
    disabled: {
      color: Color.NeutralGrey_200,
      backgroundColor: Color.White,
      borderColor: Color.NeutralGrey_200,
    },
  },
};

export const unpackButtonColorVariantStyles = (
  style: ButtonColorVariantStyles,
  theme: Theme
  // Fix return type
): { [key: string]: string | number | undefined } => ({
  ...(style.color && { color: theme.color(style.color) }),
  ...(style.backgroundColor && {
    backgroundColor: theme.color(style.backgroundColor),
  }),
  ...(style.borderColor && {
    boxShadow: `inset 0 0 0 ${
      (style.borderWidth || 0.5) * baseBorderScale // Need to create a boxshadow theme helper
    }px ${theme.color(style.borderColor)}`,
  }),
  ...(style.outlineColor && {
    outline: `${(style.outlineWidth || 0.5) *
      baseBorderScale}px solid ${theme.color(style.outlineColor)}`,
  }),
});

const sizeStyling: SizeStyling = {
  [ButtonSize.Large]: {
    paddingV: 2,
    paddingH: 6,
    fontSize: 3,
    lineHeight: '27px',
  },
  [ButtonSize.Medium]: {
    paddingV: 2,
    paddingH: 4,
    fontSize: 2,
    lineHeight: '24px',
  },
};

export const unpackSizeStyles = (size: ButtonSize, theme: Theme) => {
  return {
    ...sizeStyling[size],
    ...theme.tokens.fontSizes[sizeStyling[size].fontSize],
  };
};
